//@flow
import {useEffect, useState, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {actions} from '../duck';
import {useActions} from '../../../utils/globalStore';

import {setPageLayoutService} from '../promises';

import {useBanners} from '../../home-pages/utils/banner-group';

import {DEFAULT_HOME_PAGE_LAYOUT} from '../constants/home-layout';

import type {HomePageLayoutSection, HomePageLayout} from '../../../entities';
import type {RootState} from '../..';
import {
  NEW_BANNER_GROUP_ID,
  DYNAMIC_HOME_PAGE_SECTIONS,
} from '../../home-pages/utils/home-page-sections/constants';
import {useStoreBusinessModel} from '../../store/utils';
import {useStoreInformationBlocks} from '../../home-pages/utils/information-block';
import {useStoreLocations} from '../../home-pages/utils/location';

export function useInitializeHomePageLayout() {
  const fetchHomePageLayout = useActions(actions.fetchPageLayout);
  const updateHomePageLayout = useActions(actions.updatePageLayout);

  const {bannerGroups, loading: isFetchingBannerGroups} = useBanners();
  const {loading, informationBlocks} = useStoreInformationBlocks();
  const {locations} = useStoreLocations();

  const homePageLayout = useHomePageLayout();

  useEffect(() => {
    fetchHomePageLayout('HOME_PAGE');
  }, [fetchHomePageLayout]);

  useEffect(() => {
    if (typeof isFetchingBannerGroups === 'undefined') return;
    if (typeof loading === 'undefined') return;

    if (homePageLayout || isFetchingBannerGroups || loading) return;

    const pageLayout: HomePageLayout = {
      enabled: false,
      pageLayoutType: 'HOME_PAGE',
      layout: [],
    };

    for (const layout of DEFAULT_HOME_PAGE_LAYOUT.layout) {
      const isBannerSection = layout.pageSectionType === 'STORE_BANNER';
      const isInformationBlockSection =
        layout.pageSectionType === 'STORE_INFORMATION_BLOCK';

      if (isBannerSection) {
        bannerGroups.forEach(bannerGroup => {
          const bannerSection: HomePageLayoutSection = {
            enabled: true,
            pageSectionType: 'STORE_BANNER',
            sectionIdentifier: bannerGroup.id,
          };
          pageLayout.layout.push(bannerSection);
        });
        /* 
        if (bannerGroups.length === 0) {
          const bannerSection: HomePageLayoutSection = {
            enabled: true,
            pageSectionType: 'STORE_BANNER',
            sectionIdentifier: NEW_BANNER_GROUP_ID,
          };

          pageLayout.layout.push(bannerSection);
        } */
        continue;
      }

      if (isInformationBlockSection) {
        if (informationBlocks.length > 0) {
          informationBlocks.forEach(informationBlock => {
            const informationBlockSection: HomePageLayoutSection = {
              enabled: true,
              pageSectionType: 'STORE_INFORMATION_BLOCK',
              sectionIdentifier: informationBlock.storeInformationBlockId,
            };
            pageLayout.layout.push(informationBlockSection);
          });
        }
        continue;
      }

      pageLayout.layout.push(layout);
    }

    console.log({pageLayout});
    updateHomePageLayout({
      HOME_PAGE: pageLayout,
    });
  }, [
    loading,
    bannerGroups,
    homePageLayout,
    isFetchingBannerGroups,
    updateHomePageLayout,
    informationBlocks,
    locations,
  ]);
}

const homePageLayoutSelector = (state: RootState) =>
  state.pageLayout.layouts.HOME_PAGE;

export function useHomePageLayout() {
  const homePageLayout = useSelector(homePageLayoutSelector);

  const businessModel = useStoreBusinessModel();

  const layout = useMemo(() => {
    if (!homePageLayout) return undefined;

    const prevLayout = [...homePageLayout.layout];

    const newLayout = prevLayout.filter(section => {
      const sectionInformation =
        DYNAMIC_HOME_PAGE_SECTIONS[section.pageSectionType];

      return sectionInformation.storeBusinessModels.includes(businessModel);
    });

    return newLayout;
  }, [businessModel, homePageLayout]);

  if (!homePageLayout) return undefined;

  return {
    ...homePageLayout,
    layout,
  };
}

export function useHomePageLayoutActions() {
  const homePageLayout: HomePageLayout = useSelector(homePageLayoutSelector);
  const updateHomePageLayout = useActions(actions.updatePageLayout);
  const {bannerGroups} = useBanners();
  const [loading, setLoading] = useState(false);

  const updateHomePageLayoutService = (layout: HomePageLayoutSection[]) => {
    setLoading(true);
    setPageLayoutService({
      layout,
      enabled: homePageLayout.enabled,
      pageLayoutType: 'HOME_PAGE',
    })
      .then(res => {
        updateHomePageLayout({
          HOME_PAGE: {
            pageLayoutType: 'HOME_PAGE',
            enabled: homePageLayout.enabled,
            layout,
          },
        });
      })
      .finally(() => setLoading(false));
  };

  const getDefaultLayout = () => {
    const layout: HomePageLayoutSection[] = [];

    for (const layoutTemplate of DEFAULT_HOME_PAGE_LAYOUT.layout) {
      const isBannerSection = layoutTemplate.pageSectionType === 'STORE_BANNER';
      if (!isBannerSection) {
        layout.push(layoutTemplate);
        continue;
      }

      bannerGroups.forEach(bannerGroup => {
        const bannerSection: HomePageLayoutSection = {
          enabled: true,
          pageSectionType: 'STORE_BANNER',
          sectionIdentifier: bannerGroup.id,
        };
        if (bannerSection.sectionIdentifier !== NEW_BANNER_GROUP_ID) {
          layout.push(bannerSection);
        }
      });
    }
    return layout;
  };

  const addSection = (pageSection: HomePageLayoutSection) => {
    const isBannerSection = pageSection.pageSectionType === 'STORE_BANNER';

    if (!isBannerSection) {
      updateHomePageLayoutService([...homePageLayout.layout, pageSection]);
      return;
    }

    const existMainBannerSection = homePageLayout.layout.some(
      section =>
        section.pageSectionType === 'STORE_BANNER' &&
        section.sectionIdentifier !== NEW_BANNER_GROUP_ID
    );

    if (existMainBannerSection) {
      updateHomePageLayoutService([...homePageLayout.layout, pageSection]);
      return;
    }

    updateHomePageLayoutService([pageSection, ...homePageLayout.layout]);
  };

  const removeSection = (sectionType: string, sectionIdentifier?: string) => {
    updateHomePageLayoutService(
      homePageLayout.layout.filter((section: HomePageLayoutSection) => {
        if (!sectionIdentifier) return section.pageSectionType !== sectionType;

        return (
          section.pageSectionType !== sectionType ||
          section.sectionIdentifier !== sectionIdentifier
        );
      })
    );
  };

  const toggleEnableSection = (id: string, sectionIdentifier?: string) => {
    const newLayout = homePageLayout.layout.map(section => {
      if (section.pageSectionType !== id) return section;
      if (sectionIdentifier && section.sectionIdentifier !== sectionIdentifier)
        return section;

      return {
        pageSectionType: section.pageSectionType,
        sectionIdentifier: section.sectionIdentifier,
        enabled: !section.enabled,
      };
    });

    updateHomePageLayoutService(newLayout);
  };

  const sortSections = (sections: HomePageLayoutSection[]) => {
    updateHomePageLayoutService(sections);
  };

  const toggleEnableHomePageLayout = () => {
    setLoading(true);
    const enabled = !homePageLayout.enabled;
    const defaultLayout = getDefaultLayout();
    const newHomePageLayout = {
      enabled,
      pageLayoutType: 'HOME_PAGE',
      layout: enabled ? homePageLayout.layout : defaultLayout,
    };

    setPageLayoutService(newHomePageLayout)
      .then(res => {
        updateHomePageLayout({
          HOME_PAGE: newHomePageLayout,
        });
      })
      .finally(() => setLoading(false));
  };

  return {
    addSection,
    sortSections,
    removeSection,
    loading,
    toggleEnableSection,
    toggleEnableHomePageLayout,
  };
}
